import { subMonths } from 'date-fns';
import { form_logic } from './data/form_logic';

type OfficeCategory = 'hyperlocal' | 'local' | 'county' | 'state' | 'federal';

function getOfficeCategory(office: string): OfficeCategory {
  const officeLower = office.toLowerCase();

  // Check against form_logic categories and their seats
  if (matchesHyperLocal(officeLower)) return 'hyperlocal';
  if (matchesCityGovernment(officeLower)) return 'local';
  if (matchesCountyGovernment(officeLower)) return 'county';
  if (matchesStateGovernment(officeLower)) return 'state';
  if (matchesFederalGovernment(officeLower)) return 'federal';

  // Fallback categorization based on keywords
  if (officeLower.includes('board') || 
      officeLower.includes('trustee') || 
      officeLower.includes('commissioner') ||
      officeLower.includes('district')) return 'hyperlocal';
  
  if (officeLower.includes('city') || 
      officeLower.includes('municipal') || 
      officeLower.includes('mayor') ||
      officeLower.includes('town') ||
      officeLower.includes('village')) return 'local';
  
  if (officeLower.includes('county')) return 'county';
  
  if (officeLower.includes('state') || 
      officeLower.includes('assembly') ||
      officeLower.includes('legislature')) return 'state';
  
  if (officeLower.includes('congress') || 
      officeLower.includes('senate') ||
      officeLower.includes('federal') ||
      officeLower.includes('house')) return 'federal';

  return 'local'; // Default fallback
}

function matchesHyperLocal(office: string): boolean {
  const seats = form_logic.HyperLocal.seats;
  return Object.keys(seats).some(seat => 
    office.includes(seat.toLowerCase()) ||
    seat.toLowerCase().includes(office)
  );
}

function matchesCityGovernment(office: string): boolean {
  const seats = form_logic.CityGovernment.seats;
  return Object.keys(seats).some(seat => 
    office.includes(seat.toLowerCase()) ||
    seat.toLowerCase().includes(office)
  );
}

function matchesCountyGovernment(office: string): boolean {
  const seats = form_logic.CountyGovernment.seats;
  return Object.keys(seats).some(seat => 
    office.includes(seat.toLowerCase()) ||
    seat.toLowerCase().includes(office)
  );
}

function matchesStateGovernment(office: string): boolean {
  const seats = form_logic.StateGovernment.seats;
  return Object.keys(seats).some(seat => 
    office.includes(seat.toLowerCase()) ||
    seat.toLowerCase().includes(office)
  );
}

function matchesFederalGovernment(office: string): boolean {
  const seats = form_logic.FederalGovernment.seats;
  return Object.keys(seats).some(seat => 
    office.includes(seat.toLowerCase()) ||
    seat.toLowerCase().includes(office)
  );
}

export function getCampaignDurationMonths(office: string, electionType: string): number {
  const officeDurations: Record<OfficeCategory, number> = {
    hyperlocal: 6,
    local: 8,
    county: 12,
    state: 18,
    federal: 24,
  };

  const electionAdjustments: Record<string, number> = {
    "general-even": 0,
    "general-odd": 0,
    "primary": -4,
    "special": -6,
    "runoff": -8,
  };

  const category = getOfficeCategory(office);
  const baseMonths = officeDurations[category];
  const adjustment = electionAdjustments[electionType] ?? 0;
  
  // Special cases for specific offices
  const officeLower = office.toLowerCase();
  
  // Shorter campaigns for smaller roles
  if (officeLower.includes('school board') || 
      officeLower.includes('library') ||
      officeLower.includes('park district')) {
    return Math.max(4, baseMonths + adjustment - 2);
  }
  
  // Longer campaigns for executive positions
  if (officeLower.includes('governor') || 
      officeLower.includes('senator') ||
      officeLower.includes('president')) {
    return Math.max(6, baseMonths + adjustment + 6);
  }
  
  // Standard duration
  return Math.max(3, baseMonths + adjustment);
}

export function getCampaignStartDate(electionDate: Date, office: string, electionType: string): Date {
  const monthsBack = getCampaignDurationMonths(office, electionType);
  return subMonths(electionDate, monthsBack);
}